import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Activity,
  Shield,
  BarChart3,
  Bell,
  Globe,
  Zap,
  CheckCircle,
  ArrowRight,
  MousePointer2,
  TrendingUp
} from 'lucide-react';

const Home = () => {
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: Zap,
      title: 'Real-time Monitoring',
      description: 'Ultra-fast checks from multiple global locations with sub-second accuracy.',
      color: 'brand'
    },
    {
      icon: Bell,
      title: 'Instant Alerts',
      description: 'Receive multi-channel notifications via Email, Webhooks, and SMS instantly.',
      color: 'success'
    },
    {
      icon: BarChart3,
      title: 'Deep Insights',
      description: 'Visualize response time trends and historical uptime with beautiful charts.',
      color: 'info'
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Bank-grade encryption and secure monitoring nodes for your peace of mind.',
      color: 'brand'
    }
  ];

  const stats = [
    { label: 'Uptime Guarantee', value: '99.99%' },
    { label: 'Monitoring Nodes', value: '24' },
    { label: 'Avg. Response', value: '240ms' },
    { label: 'Alerts Delivered', value: '1M+' }
  ];

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-950 transition-colors duration-500 overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 lg:pt-48 lg:pb-40 mesh-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center animate-slide-up">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-brand-500/10 border border-brand-500/20 mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-500"></span>
              </span>
              <span className="text-xs font-bold uppercase tracking-wider text-brand-600 dark:text-brand-400">
                New: Global Status Pages 2.0
              </span>
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-surface-900 dark:text-white mb-8 leading-[1.1]">
              Monitor Your Infrastructure <br />
              <span className="text-gradient">With Precision</span>
            </h1>

            <p className="text-lg md:text-xl text-surface-600 dark:text-surface-400 mb-12 max-w-2xl mx-auto leading-relaxed">
              Elevate your monitoring with real-time analytics, instant multi-channel alerts,
              and a dashboard designed for modern engineering teams.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {isAuthenticated ? (
                <Link to="/dashboard" className="btn-primary px-8 py-4 text-lg">
                  Access Dashboard <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              ) : (
                <>
                  <Link to="/register" className="btn-primary px-8 py-4 text-lg group">
                    Start Free Trial
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link to="/login" className="btn-secondary px-8 py-4 text-lg">
                    Sign In
                  </Link>
                </>
              )}
            </div>

            {/* Visual Element: Dashboard Preview Card */}
            <div className="mt-20 relative px-4 lg:px-0">
              <div className="absolute inset-0 bg-brand-500/10 blur-[120px] rounded-full mx-auto max-w-4xl opacity-50"></div>
              <div className="glass-card rounded-3xl p-4 lg:p-8 animate-float relative border border-white/20 dark:border-surface-800 shadow-premium-xl">
                <div className="flex items-center justify-between mb-8 border-b border-surface-200 dark:border-surface-800 pb-4">
                  <div className="flex space-x-2">
                    <div className="h-3 w-3 rounded-full bg-danger/50"></div>
                    <div className="h-3 w-3 rounded-full bg-warning/50"></div>
                    <div className="h-3 w-3 rounded-full bg-success/50"></div>
                  </div>
                  <div className="px-4 py-1.5 rounded-full bg-surface-100 dark:bg-surface-900 border border-surface-200 dark:border-surface-800 text-[10px] font-mono text-surface-500 dark:text-surface-400 flex items-center space-x-2">
                    <Globe className="h-3 w-3" />
                    <span>uptime-dashboard.io/metrics</span>
                  </div>
                  <div className="text-xs font-bold text-success flex items-center space-x-1">
                    <div className="h-2 w-2 rounded-full bg-success animate-pulse"></div>
                    <span>SYSTEMS ONLINE</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                  {/* Mock Stat 1 */}
                  <div className="p-6 rounded-2xl bg-surface-50 dark:bg-surface-900/50 border border-surface-200 dark:border-surface-800">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] font-bold text-surface-500 uppercase tracking-widest">Global Uptime</p>
                      <TrendingUp className="h-3 w-3 text-success" />
                    </div>
                    <p className="text-3xl font-bold text-surface-900 dark:text-white">99.98<span className="text-surface-400 font-normal">%</span></p>
                    <div className="mt-4 flex space-x-1 h-3 items-end">
                      {[40, 70, 45, 90, 65, 80, 50, 95, 75, 85].map((h, i) => (
                        <div key={i} className="flex-1 bg-brand-500/20 dark:bg-brand-500/10 rounded-t-sm" style={{ height: `${h}%` }}></div>
                      ))}
                    </div>
                  </div>

                  {/* Mock Stat 2 */}
                  <div className="p-6 rounded-2xl bg-surface-50 dark:bg-surface-900/50 border border-surface-200 dark:border-surface-800">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] font-bold text-surface-500 uppercase tracking-widest">Avg Response</p>
                      <Zap className="h-3 w-3 text-warning" />
                    </div>
                    <p className="text-3xl font-bold text-surface-900 dark:text-white text-gradient">124<span className="text-surface-400 font-normal ml-1">ms</span></p>
                    <div className="mt-4 flex items-center space-x-2">
                      <div className="flex-1 h-1.5 bg-surface-200 dark:bg-surface-800 rounded-full overflow-hidden">
                        <div className="w-[70%] h-full bg-brand-500 rounded-full"></div>
                      </div>
                      <span className="text-[10px] font-bold text-surface-400 tracking-tighter">FAST</span>
                    </div>
                  </div>

                  {/* Mock Stat 3 */}
                  <div className="p-6 rounded-2xl bg-surface-50 dark:bg-surface-900/50 border border-surface-200 dark:border-surface-800">
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-[10px] font-bold text-surface-500 uppercase tracking-widest">Node Health</p>
                    </div>
                    <div className="space-y-3">
                      {['US-East', 'EU-West', 'AS-North'].map((city) => (
                        <div key={city} className="flex items-center justify-between">
                          <span className="text-xs font-medium text-surface-600 dark:text-surface-400">{city}</span>
                          <div className="flex space-x-1">
                            {[1, 2, 3, 4, 5].map(i => (
                              <div key={i} className="h-1.5 w-1.5 rounded-full bg-success"></div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white dark:bg-surface-900/50 border-y border-surface-200 dark:border-surface-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="text-4xl font-bold text-surface-900 dark:text-white mb-2 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                  {stat.value}
                </div>
                <div className="text-sm font-semibold tracking-widest uppercase text-surface-500 dark:text-surface-500">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20 animate-slide-up">
            <h2 className="text-3xl md:text-5xl font-bold text-surface-900 dark:text-white mb-6">
              Powerful Features for <br /> Modern Teams
            </h2>
            <p className="text-lg text-surface-600 dark:text-surface-400 max-w-2xl mx-auto">
              Everything you need to maintain 99.99% reliability across your entire tech stack.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="card-premium p-8 group hover:-translate-y-2"
              >
                <div className={`inline-flex p-3 rounded-2xl bg-${feature.color}-500/10 mb-6 group-hover:bg-${feature.color}-500/20 transition-colors`}>
                  <feature.icon className={`h-8 w-8 text-brand-600 dark:text-brand-400`} />
                </div>
                <h3 className="text-xl font-bold text-surface-900 dark:text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-surface-600 dark:text-surface-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-[3rem] p-12 lg:p-24 overflow-hidden shadow-premium-xl bg-surface-900 border border-surface-800 animate-scale-in">
            {/* Decorative Background Elements */}
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-[30rem] h-[30rem] bg-brand-500/20 blur-[120px] rounded-full"></div>
            <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-[30rem] h-[30rem] bg-brand-600/10 blur-[120px] rounded-full"></div>

            <div className="relative z-10 text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-8">
                <span className="text-[10px] font-bold uppercase tracking-widest text-brand-400">Get Started Today</span>
              </div>

              <h2 className="text-4xl md:text-6xl font-bold text-white mb-8 leading-[1.1]">
                Stop Guessing. <br />
                <span className="text-brand-400">Start Monitoring.</span>
              </h2>

              <p className="text-lg md:text-xl text-surface-400 mb-12 leading-relaxed">
                Join 10,000+ developers who trust our enterprise-grade monitoring system
                to keep their services running 24/7 with 99.99% reliability.
              </p>

              {!isAuthenticated && (
                <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                  <Link
                    to="/register"
                    className="btn-primary !py-5 !px-12 text-lg shadow-glow-brand hover:scale-105 transition-transform"
                  >
                    Start Free Trial
                  </Link>
                  <div className="text-left">
                    <p className="text-white font-bold text-sm">No credit card required</p>
                    <p className="text-surface-500 text-xs">Full access for 14 days • Cancel anytime</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer (Simple & Professional) */}
      <footer className="py-12 border-t border-surface-200 dark:border-surface-800">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Activity className="h-6 w-6 text-brand-600" />
            <span className="font-bold text-xl tracking-tight dark:text-white">UptimeMonitor</span>
          </div>
          <p className="text-surface-500 text-sm">
            &copy; {new Date().getFullYear()} UptimeMonitor. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
